import { useState } from 'react';
import { motion } from 'framer-motion';
import { Warehouse, Search, AlertTriangle, Package, ArrowUp, ArrowDown, ArrowRight, BarChart3, Plus, X } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, formatDate } from '../utils/helpers';
import { toast } from '../components/Toast';

export default function InventoryPage() {
  const { products, updateProduct } = useAdminStore();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'stock' | 'alerts' | 'movements'>('stock');
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStockForm, setAddStockForm] = useState({ productId: '', quantity: 0, reference: '' });
  const [formError, setFormError] = useState('');

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()));
  const lowStockProducts = products.filter(p => (p.stock - p.reservedStock) <= 5);
  const totalStock = products.reduce((s, p) => s + p.stock, 0);
  const totalValue = products.reduce((s, p) => s + p.stock * (p.costPrice || 0), 0);
  const totalReserved = products.reduce((s, p) => s + p.reservedStock, 0);

  const movements = [
    { id: 'SM-1', productName: 'Hikvision DS-2CD2T47G2-LS', type: 'out' as const, quantity: 3, reference: 'INV-202605-0001', date: '2026-05-15T00:00:00Z', performedBy: 'Kasun Perera' },
    { id: 'SM-2', productName: 'Dahua HAC-HDW1500TLP-A-LED', type: 'in' as const, quantity: 20, reference: 'PO-202606-001', date: '2026-06-15T00:00:00Z', performedBy: 'Admin' },
    { id: 'SM-3', productName: 'Ubiquiti UniFi AP AC Pro', type: 'out' as const, quantity: 4, reference: 'INV-202606-003', date: '2026-06-20T00:00:00Z', performedBy: 'Nipun Silva' },
    { id: 'SM-4', productName: 'Starlink Standard Kit', type: 'out' as const, quantity: 1, reference: 'INV-202606-006', date: '2026-06-25T00:00:00Z', performedBy: 'Admin' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Inventory</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{products.length} products across all categories</p>
        </div>
        <button onClick={() => setShowAddStock(true)} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2" aria-label="Add stock">
          <Plus className="w-4 h-4" />Add Stock
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Stock', value: totalStock.toString(), icon: Package, color: 'from-blue-400 to-blue-600' },
          { label: 'Available', value: (totalStock - totalReserved).toString(), icon: Warehouse, color: 'from-emerald-400 to-emerald-600' },
          { label: 'Reserved', value: totalReserved.toString(), icon: ArrowRight, color: 'from-amber-400 to-amber-600' },
          { label: 'Stock Value', value: formatCurrency(totalValue), icon: BarChart3, color: 'from-violet-400 to-violet-600' },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div key={item.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-4">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className="text-[18px] font-bold text-gray-900">{item.value}</div>
              <div className="text-[12px] text-gray-400 mt-0.5">{item.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden w-fit">
        {(['stock', 'alerts', 'movements'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 h-10 text-[13px] font-medium capitalize transition-all ${activeTab === tab ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>{tab}</button>
        ))}
      </div>

      {activeTab === 'stock' && (
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 overflow-hidden">
          {filtered.length === 0 && search && (
            <div className="p-12 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[14px] font-medium text-gray-500">No products match &ldquo;{search}&rdquo;</p>
              <p className="text-[12px] text-gray-400 mt-1">Try a different search term or SKU</p>
            </div>
          )}
          {filtered.length === 0 && !search && (
            <div className="p-12 text-center">
              <Warehouse className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[14px] font-medium text-gray-500">No inventory items yet</p>
              <p className="text-[12px] text-gray-400 mt-1">Add products to start tracking stock</p>
            </div>
          )}
          <div className="p-4 border-b border-gray-100">
            <div className="relative"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search stock..."
                className="w-full h-10 pl-10 pr-4 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
            </div>
          </div>
          <table className="w-full">
            <thead><tr className="border-b border-gray-100">
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Product</th>
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Current</th>
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Available</th>
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Reserved</th>
              <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Value</th>
            </tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-5 py-3">
                    <div className="text-[13px] font-medium text-gray-900">{p.name}</div>
                    <div className="text-[11px] text-gray-400">{p.sku}</div>
                  </td>
                  <td className="px-5 py-3 text-[13px] text-gray-700 text-right">{p.stock}</td>
                  <td className="px-5 py-3 text-right"><span className={`text-[13px] font-medium ${(p.stock - p.reservedStock) <= 5 ? 'text-red-500' : 'text-emerald-600'}`}>{p.stock - p.reservedStock}</span></td>
                  <td className="px-5 py-3 text-[13px] text-gray-500 text-right">{p.reservedStock}</td>
                  <td className="px-5 py-3 text-[13px] font-medium text-gray-900 text-right">{formatCurrency(p.stock * (p.costPrice || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {lowStockProducts.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-8 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[14px] text-gray-500 font-medium">All stock levels are healthy</p>
            </div>
          ) : lowStockProducts.map(p => (
            <div key={p.id} className="bg-white/80 backdrop-blur-xl rounded-2xl border border-red-200/60 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
              <div className="flex-1">
                <div className="text-[13px] font-medium text-gray-900">{p.name}</div>
                <div className="text-[12px] text-gray-400">{p.sku} · Available: {p.stock - p.reservedStock}</div>
              </div>
              <button className="h-8 px-3 rounded-lg bg-gray-900 text-white text-[11px] font-medium hover:bg-gray-800">Reorder</button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'movements' && (
        <div className="space-y-3">
          {movements.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.type === 'in' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {m.type === 'in' ? <ArrowDown className="w-5 h-5 text-emerald-500" /> : <ArrowUp className="w-5 h-5 text-red-500" />}
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-medium text-gray-900">{m.productName}</div>
                <div className="text-[12px] text-gray-400">{m.reference} · {m.performedBy}</div>
              </div>
              <div className="text-right">
                <div className={`text-[14px] font-semibold ${m.type === 'in' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {m.type === 'in' ? '+' : '-'}{m.quantity}
                </div>
                <div className="text-[11px] text-gray-400">{formatDate(m.date)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      {/* Add Stock Modal */}
      {showAddStock && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddStock(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">Add Stock</h2>
              <button onClick={() => setShowAddStock(false)} className="p-1.5 rounded-xl hover:bg-gray-100" aria-label="Close"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {formError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-[12px] text-red-600">{formError}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Product *</label>
                <select value={addStockForm.productId} onChange={e => setAddStockForm({ ...addStockForm, productId: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5">
                  <option value="">Select product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} (Current: {p.stock})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Quantity *</label>
                <input type="number" min="1" value={addStockForm.quantity || ''} onChange={e => setAddStockForm({ ...addStockForm, quantity: Number(e.target.value) })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Reference (PO number)</label>
                <input value={addStockForm.reference} onChange={e => setAddStockForm({ ...addStockForm, reference: e.target.value })}
                  placeholder="e.g. PO-2026-001"
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddStock(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={() => {
                setFormError('');
                if (!addStockForm.productId) { setFormError('Please select a product.'); return; }
                if (addStockForm.quantity <= 0) { setFormError('Quantity must be greater than 0.'); return; }
                const product = products.find(p => p.id === addStockForm.productId);
                if (product) {
                  updateProduct(product.id, { stock: product.stock + addStockForm.quantity, updatedAt: new Date().toISOString() });
                  toast.success('Stock Added', `Added ${addStockForm.quantity} units to ${product.name}`);
                  setAddStockForm({ productId: '', quantity: 0, reference: '' });
                  setShowAddStock(false);
                }
              }} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800">Add Stock</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
